import {
  EnhancedExamSessionResults,
  ExamConfigData,
  ParsedExam,
  ParsedExamSession,
  ParsedItem,
  Resource,
  Score,
} from "@examspark/types";

import {
  getParsedExamSessionResponse,
  getReadableScoringDataForItem,
} from "./content/items/answerKeys";
import { forEachItemCanvasElement } from "./item";
import { fromAWSDate } from "./utils/coursePlanner";
import { filterMaybeArray, generateCode } from "./utils/misc";

export const getExamItemIdAtPosition = (
  exam: Resource | undefined,
  formIndex: number,
  sectionIndex: number,
  itemIndex: number,
) => {
  const form = exam?.content?.forms?.[formIndex];
  const section = form?.sections?.[sectionIndex];
  const itemId = section?.questionIds?.[itemIndex];

  return itemId;
};

export const getOrderedExamItemIds = (
  exam: {
    content?: Resource["content"];
  },
  formIndex: number,
) => {
  const form = exam.content?.forms?.[formIndex];
  if (!form) return [];

  const itemIds =
    form.sections?.flatMap((section) =>
      filterMaybeArray(section?.questionIds),
    ) ?? [];
  return itemIds;
};

export const getExamItemIdsAtFormSectionPosition = (
  exam: Resource,
  formIndex: number,
  sectionIndex: number,
) => {
  const itemIds =
    exam.content?.forms?.[formIndex]?.sections?.[sectionIndex]?.questionIds ??
    [];

  return itemIds;
};

export const generateExamTicketCode = () => {
  return generateCode();
};

type ScorableResource = Pick<
  Resource,
  "content" | "questions" | "workspaceId" | "id" | "sk"
>;

export const iterateExamItems = (
  exam: ScorableResource,
  itemCallback: (item: Resource) => void,
) => {
  for (const form of exam.content?.forms ?? []) {
    if (!form?.sections?.length) continue;
    for (const section of form?.sections ?? []) {
      if (!section?.questionIds?.length) continue;
      for (const questionId of section?.questionIds ?? []) {
        if (!questionId) continue;
        const question = exam.questions?.find((q) => q?.id === questionId);
        if (!question) continue;
        itemCallback(question);
      }
    }
  }
};

export const getExamItemsFromSection = (
  exam: ParsedExam,
  formIndex: number,
  sectionIndex: number,
) => {
  const form = exam.content?.forms?.[formIndex];
  const section = form?.sections?.[sectionIndex];
  const itemIds = section?.questionIds ?? [];
  return itemIds
    .map((id) => exam.questions?.find((q) => q?.id === id))
    .filter(Boolean);
};

export const addPointsToActualScore = (score: Score, points?: number) => {
  if (points == null) {
    return score;
  } else {
    if (score.actual == null) score.actual = 0;
    score.actual += points;
  }
};

export const validateAccessDates = (examConfigConfig: ExamConfigData) => {
  if (
    examConfigConfig.disableAccessBeforeEnabled &&
    examConfigConfig.disableAccessBefore
  ) {
    // backwards-compatible with old date format (prefer Z dates)
    const date = examConfigConfig.disableAccessBefore.match(/Z$/)
      ? fromAWSDate(examConfigConfig.disableAccessBefore)
      : new Date(examConfigConfig.disableAccessBefore);

    if (date > new Date()) {
      throw new Error(`This activity is not available yet.`);
    }
  }
  if (
    examConfigConfig.disableAccessOnOrAfterEnabled &&
    examConfigConfig.disableAccessOnOrAfter
  ) {
    // backwards-compatible with old date format (prefer Z dates)
    const date = examConfigConfig.disableAccessOnOrAfter.match(/Z$/)
      ? fromAWSDate(examConfigConfig.disableAccessOnOrAfter)
      : new Date(examConfigConfig.disableAccessOnOrAfter);

    if (date < new Date()) {
      throw new Error(`This activity is no longer available.`);
    }
  }
};

export const getPercentScore = (pointsActual: number, pointsMax: number) => {
  const base = pointsActual / pointsMax || 0;
  return Math.round(base * 100);
};

type QuestionResponse = {
  options: { index: number }[];
};

export const getResponseInExamSession = (
  examSession: ParsedExamSession,
  itemId: string,
  questionId?: string,
) => {
  if (questionId) {
    return examSession.examSessionResponses?.[itemId]?.[
      questionId
    ] as QuestionResponse;
  }

  return examSession.examSessionResponses?.[itemId] as Record<
    string,
    QuestionResponse
  >;
};

/** Returns `examSession` with human-readable `results` */
export const examSessionResultsItems = (
  _examSession: ParsedExamSession,
  params: {
    items: ParsedItem[];
  },
) => {
  const examSession = JSON.parse(JSON.stringify(_examSession));

  const { items } = params;
  if (examSession.examSessionResults) {
    // expand `examSessionResults` new results object with enhanced props
    const examSessionResults: EnhancedExamSessionResults =
      examSession.examSessionResults;

    // responses
    const examSessionResultItems = examSessionResults.items ?? [];
    for (const examSessionResultItem of examSessionResultItems ?? []) {
      const item = items.find((i) => i.id === examSessionResultItem.id);
      if (!item) continue;
      const itemScoring = getReadableScoringDataForItem(item);

      forEachItemCanvasElement(item, (canvasElement) => {
        if (!canvasElement.id) return;
        examSessionResultItem.questions = itemScoring;

        // read .examSessionResponses
        const questionResponse = getResponseInExamSession(
          examSession,
          item.id,
          canvasElement.id,
        );
        for (const question of examSessionResultItem.questions) {
          question.response = {
            raw: questionResponse,
            processed: getParsedExamSessionResponse(question, questionResponse),
          };
        }
      });
    }

    return examSession;
  }

  // fallback to item data, not result data, since its not ready yet.
  // expand `examSessionResults` new results object with enhanced props
  const examSessionResults: EnhancedExamSessionResults = {
    items: [],
    objectives: {},
  };

  for (const item of items ?? []) {
    const resultItem: EnhancedExamSessionResults["items"][0] = {
      id: item.id,
      sk: item.sk,
      alignmentIds: filterMaybeArray(item.alignmentIds) ?? [],
      name: item.name ?? "",
      questions: [],
    };

    const itemScoring = getReadableScoringDataForItem(item);

    forEachItemCanvasElement(item, (canvasElement) => {
      if (!canvasElement.id) return;
      resultItem.questions = itemScoring;

      const questionResponse = getResponseInExamSession(
        examSession,
        item.id,
        canvasElement.id,
      );
      for (const question of resultItem.questions) {
        question.response = {
          raw: questionResponse,
          processed: getParsedExamSessionResponse(question, questionResponse),
        };
      }
    });

    examSessionResults.items.push(resultItem);
  }

  examSession.examSessionResults = examSessionResults;
  return examSession;
};
